import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Tree,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Space,
  message,
  Alert,
  Popconfirm,
  Row,
  Col,
  Badge,
  Table,
  Switch,
  Tag,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { categoryAPI, policyAPI, versionAPI, deviceAPI } from '../utils/api';
import { Category, Policy, Version, Device, CreateCategoryRequest, UpdateCategoryRequest } from '../types';
import type { TreeProps } from 'antd/es/tree';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title, Text } = Typography;
const { Option } = Select;

interface TreeNode {
  key: string;
  title: React.ReactNode;
  children?: TreeNode[];
  category: Category;
  totalDeviceCount?: number;
}

interface DeviceStats {
  total: number;
  online: number;
  recent: number;
  offline: number;
  disabled: number;
}

const Categories: React.FC = () => {
  // === 目录树相关状态 ===
  const [categories, setCategories] = useState<Category[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [selectedParent, setSelectedParent] = useState<Category | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  // === 设备列表相关状态 ===
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('');
  const [includeChildren, setIncludeChildren] = useState(false);
  const [categoryDevices, setCategoryDevices] = useState<Device[]>([]);
  const [deviceStats, setDeviceStats] = useState<DeviceStats>({ total: 0, online: 0, recent: 0, offline: 0, disabled: 0 });
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loadingDevices, setLoadingDevices] = useState(false);

  // === 批量移动相关状态 ===
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [targetCategoryId, setTargetCategoryId] = useState<number | null>(null);
  const [movingDevices, setMovingDevices] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // 当选中目录或includeChildren变化时，获取设备列表
  useEffect(() => {
    if (selectedCategoryId) {
      fetchCategoryDevices(selectedCategoryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, includeChildren]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [categoriesData, policiesData, versionsData] = await Promise.all([
        categoryAPI.getCategoryTree(),
        policyAPI.getPolicies(),
        versionAPI.getVersions()
      ]);

      setCategories(categoriesData);
      setPolicies(policiesData);
      setVersions(versionsData);
    } catch (err: any) {
      setError(err.message || '获取数据失败');
      console.error('获取数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryDevices = async (categoryId: number) => {
    try {
      setLoadingDevices(true);
      const result = await categoryAPI.getCategoryDevices(categoryId, { includeChildren });
      setCategoryDevices(result.devices);
      setDeviceStats(result.stats);
      setSelectedDeviceIds([]); // 清空选中
    } catch (err: any) {
      message.error(err.message || '获取设备列表失败');
      console.error('获取设备列表失败:', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  // 获取设备状态值
  const getDeviceStatusValue = (device: Device): string => {
    if (device.disabled) return 'disabled';
    if (device.lastCommunicationTime) {
      const lastCommLocal = dayjs.utc(device.lastCommunicationTime).local();
      const hoursSinceComm = dayjs().diff(lastCommLocal, 'hour');
      if (hoursSinceComm < 24) return 'online';
      if (hoursSinceComm < 72) return 'recent';
    }
    return 'offline';
  };

  // 根据状态过滤设备
  const filteredDevices = useMemo(() => {
    if (statusFilter === 'all') return categoryDevices;
    return categoryDevices.filter(device => getDeviceStatusValue(device) === statusFilter);
  }, [categoryDevices, statusFilter]);

  // 搜索过滤逻辑 - 直接更新树数据
  const applySearch = (searchText: string) => {
    if (!searchText.trim()) {
      setTreeData(buildTreeData(categories));
      return;
    }

    const filtered = categories.filter(category =>
      category.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (category.description || '').toLowerCase().includes(searchText.toLowerCase())
    );

    const relatedCategories = new Set<Category>();
    filtered.forEach(category => relatedCategories.add(category));

    // 添加父级目录 - 使用辅助函数避免循环中的闭包问题
    const findAncestors = (cat: Category) => {
      let currentCat = cat;
      while (currentCat.parentId) {
        const parentIdToFind = currentCat.parentId; // 捕获当前值避免闭包问题
        const parentCat = categories.find(c => c.id === parentIdToFind);
        if (parentCat) {
          relatedCategories.add(parentCat);
          currentCat = parentCat;
        } else {
          break;
        }
      }
    };
    filtered.forEach(category => findAncestors(category));

    // 添加子级目录
    const addChildren = (parentId: number) => {
      const children = categories.filter(c => c.parentId === parentId);
      children.forEach(child => {
        relatedCategories.add(child);
        addChildren(child.id);
      });
    };
    filtered.forEach(category => addChildren(category.id));

    const finalFiltered = Array.from(relatedCategories);
    setTreeData(buildTreeData(finalFiltered));
    setExpandedKeys(finalFiltered.map(c => c.id.toString()));
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    applySearch(value);
  };

  const buildTreeData = (items: Category[], parentId?: number): TreeNode[] => {
    return items
      .filter(item => {
        if (parentId === undefined) {
          return item.parentId === null || item.parentId === undefined;
        }
        return item.parentId === parentId;
      })
      .map(item => {
        const children = buildTreeData(items, item.id);
        const currentDeviceCount = item.deviceCount || 0;
        const childrenDeviceCount = children.reduce((sum, child) => sum + (child.totalDeviceCount || 0), 0);
        const totalDeviceCount = currentDeviceCount + childrenDeviceCount;

        return {
          key: item.id.toString(),
          title: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FolderOutlined />
                <span title={item.description}>{item.name}</span>
                {totalDeviceCount > 0 && (
                  <Badge
                    count={totalDeviceCount}
                    style={{ marginLeft: 8, backgroundColor: '#52c41a' }}
                    size="small"
                  />
                )}
              </span>
              <Space size="small">
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    showModal(undefined, item);
                  }}
                  title="添加子目录"
                />
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    showModal(item);
                  }}
                  title="编辑目录"
                />
                <Popconfirm
                  title="确定要删除这个目录吗？"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    handleDelete(item.id);
                  }}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button
                    type="link"
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    title="删除目录"
                    onClick={(e) => e?.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            </div>
          ),
          children: children.length > 0 ? children : undefined,
          category: item,
          totalDeviceCount: totalDeviceCount
        };
      });
  };

  useEffect(() => {
    if (!searchValue) {
      setTreeData(buildTreeData(categories));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, searchValue]);

  const showModal = (category?: Category, parent?: Category) => {
    setEditingCategory(category || null);
    setSelectedParent(parent || null);
    setModalVisible(true);
    
    if (category) {
      form.setFieldsValue({
        name: category.name,
        description: category.description,
        parentId: category.parentId,
        policyId: category.policyId,
        versionId: category.versionId,
      });
    } else {
      if (parent) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue({ parentId: parent.id });
        }, 0);
      } else {
        form.resetFields();
      }
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingCategory(null);
    setSelectedParent(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      const formData: any = { ...values };
      
      if (editingCategory) {
        if (editingCategory.policyId != null && values.policyId === undefined) {
          formData.policyId = null;
        }
        if (editingCategory.versionId != null && values.versionId === undefined) {
          formData.versionId = null;
        }
      }

      if (editingCategory) {
        const updateData: UpdateCategoryRequest = { id: editingCategory.id, ...formData };
        await categoryAPI.updateCategory(updateData);
        message.success('目录更新成功');
      } else {
        const createData: CreateCategoryRequest = formData;
        await categoryAPI.createCategory(createData);
        message.success('目录创建成功');
      }

      setModalVisible(false);
      if (!editingCategory && selectedParent) {
        setExpandedKeys(prev => {
          const newKeys = [...prev];
          const parentKey = selectedParent.id.toString();
          if (!newKeys.includes(parentKey)) {
            newKeys.push(parentKey);
          }
          return newKeys;
        });
      }
      setEditingCategory(null);
      setSelectedParent(null);
      form.resetFields();
      await fetchData();
    } catch (err: any) {
      message.error(err.message || '操作失败');
      console.error('操作失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await categoryAPI.deleteCategory(id);
      message.success('目录删除成功');
      if (selectedCategoryId === id) {
        setSelectedCategoryId(null);
        setSelectedCategoryName('');
        setCategoryDevices([]);
        setDeviceStats({ total: 0, online: 0, recent: 0, offline: 0, disabled: 0 });
      }
      await fetchData();
    } catch (err: any) {
      message.error(err.message || '删除失败');
      console.error('删除失败:', err);
    }
  };

  const onExpand: TreeProps['onExpand'] = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
  };

  // 处理目录节点选择
  const onTreeSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
    if (selectedKeys.length > 0) {
      const categoryId = parseInt(selectedKeys[0] as string);
      const node = info.node as any;
      setSelectedCategoryId(categoryId);
      setSelectedCategoryName(node.category?.name || '');
      setStatusFilter('all');
    }
  };

  // 获取设备状态标签
  const getDeviceStatusTag = (device: Device) => {
    const status = getDeviceStatusValue(device);
    switch (status) {
      case 'online':
        return <Tag color="green">在线</Tag>;
      case 'recent':
        return <Tag color="orange">最近活跃</Tag>;
      case 'offline':
        return <Tag color="default">离线</Tag>;
      case 'disabled':
        return <Tag color="red">已禁用</Tag>;
      default:
        return <Tag>未知</Tag>;
    }
  };

  // 获取目录路径 - 使用辅助变量避免循环中的闭包警告
  const getCategoryPath = (categoryId: number): string => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return '';
    const path = [category.name];
    let currentCat = category;
    while (currentCat.parentId) {
      const parentIdToFind = currentCat.parentId; // 捕获当前值避免闭包问题
      const parentCat = categories.find(c => c.id === parentIdToFind);
      if (parentCat) {
        path.unshift(parentCat.name);
        currentCat = parentCat;
      } else break;
    }
    return path.join(' / ');
  };

  // 设备列表表格列定义
  const deviceColumns: ColumnsType<Device> = [
    {
      title: '设备代码',
      dataIndex: 'deviceCode',
      key: 'deviceCode',
      width: 160,
      sorter: (a, b) => a.deviceCode.localeCompare(b.deviceCode),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => getDeviceStatusTag(record),
      sorter: (a, b) => {
        const statusOrder = { online: 0, recent: 1, offline: 2, disabled: 3 };
        const getOrder = (d: Device) => statusOrder[getDeviceStatusValue(d) as keyof typeof statusOrder] ?? 4;
        return getOrder(a) - getOrder(b);
      },
    },
    {
      title: '用户身份',
      dataIndex: 'userIdentity',
      key: 'userIdentity',
      width: 120,
      render: (text) => text || '-',
      sorter: (a, b) => (a.userIdentity || '').localeCompare(b.userIdentity || ''),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-',
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
    },
    {
      title: '实际版本',
      dataIndex: 'currentVersionCode',
      key: 'currentVersionCode',
      width: 110,
      sorter: (a, b) => (a.currentVersionCode || 0) - (b.currentVersionCode || 0),
      render: (value: number | null, record: Device) => {
        if (!value) return '-';
        const versionStr = `v${value}`;
        // 获取设备的目标版本（设备自身 > 目录继承）
        const targetVersion = record.versionId
          ? versions.find(v => v.id === record.versionId)
          : null;
        // 如果有目标版本，比较是否一致
        if (targetVersion) {
          if (value === targetVersion.versionCode) {
            return <Tag color="green">{versionStr}</Tag>;
          } else if (value < targetVersion.versionCode) {
            return <Tag color="orange">{versionStr}</Tag>;
          } else {
            return <Tag color="blue">{versionStr}</Tag>;
          }
        }
        return versionStr;
      },
    },
    {
      title: '最后通信',
      dataIndex: 'lastCommunicationTime',
      key: 'lastCommunicationTime',
      width: 160,
      sorter: (a, b) => {
        const timeA = a.lastCommunicationTime ? new Date(a.lastCommunicationTime).getTime() : 0;
        const timeB = b.lastCommunicationTime ? new Date(b.lastCommunicationTime).getTime() : 0;
        return timeA - timeB;
      },
      defaultSortOrder: 'descend',
      render: (text) => text ? dayjs.utc(text).local().format('MM-DD HH:mm') : '-',
    },
  ];

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys: selectedDeviceIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedDeviceIds(selectedRowKeys as number[]);
    },
  };

  // 打开移动Modal
  const openMoveModal = () => {
    if (selectedDeviceIds.length === 0) {
      message.warning('请先选择要移动的设备');
      return;
    }
    setTargetCategoryId(null);
    setMoveModalVisible(true);
  };

  // 执行批量移动
  const handleBatchMove = async () => {
    if (!targetCategoryId) {
      message.warning('请选择目标目录');
      return;
    }
    
    if (targetCategoryId === selectedCategoryId && !includeChildren) {
      message.warning('目标目录不能与当前目录相同');
      return;
    }

    Modal.confirm({
      title: '确认移动设备',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>将 <strong>{selectedDeviceIds.length}</strong> 台设备移动到：</p>
          <p><strong>{getCategoryPath(targetCategoryId)}</strong></p>
        </div>
      ),
      okText: '确认移动',
      cancelText: '取消',
      onOk: async () => {
        try {
          setMovingDevices(true);
          const result = await deviceAPI.batchMoveDevices(
            selectedDeviceIds, 
            targetCategoryId,
            selectedCategoryId || undefined
          );
          
          message.success(result.message);
          setMoveModalVisible(false);
          setSelectedDeviceIds([]);
          
          // 刷新数据
          await fetchData();
          if (selectedCategoryId) {
            await fetchCategoryDevices(selectedCategoryId);
          }
        } catch (err: any) {
          message.error(err.response?.data?.error || '移动失败');
          console.error('批量移动失败:', err);
        } finally {
          setMovingDevices(false);
        }
      }
    });
  };

  // 扁平化目录树用于选择器
  const flattenCategories = (items: Category[]): { category: Category; level: number }[] => {
    const result: { category: Category; level: number }[] = [];
    const rootItems = items.filter(item => !item.parentId);
    
    const processItem = (item: Category, currentLevel: number) => {
      result.push({ category: item, level: currentLevel });
      const children = items.filter(c => c.parentId === item.id);
      children.forEach(child => processItem(child, currentLevel + 1));
    };
    
    rootItems.forEach(item => processItem(item, 0));
    return result;
  };

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          📁 目录列表
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>
          树状结构管理设备目录分类，点击目录查看和管理设备
        </p>
      </div>

      <Row gutter={16}>
        {/* 左侧：目录树 */}
        <Col span={8}>
          <Card
            className="content-card"
            title={
              <Space>
                <FolderOutlined />
                目录树
              </Space>
            }
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchData}
                  loading={loading}
                  size="small"
                >
                  刷新
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => showModal()}
                  size="small"
                >
                  新增根目录
                </Button>
              </Space>
            }
            bodyStyle={{ padding: '12px' }}
          >
            {/* 搜索框 */}
            <div style={{ marginBottom: 12 }}>
              <Input.Search
                placeholder="搜索目录..."
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
                allowClear
                size="small"
              />
            </div>
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>加载中...</div>
            ) : (
              <Tree
                showLine
                showIcon={false}
                defaultExpandAll
                expandedKeys={expandedKeys}
                onExpand={onExpand}
                onSelect={onTreeSelect}
                treeData={treeData}
                style={{ background: '#fafafa', padding: 8, borderRadius: 6, maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}
              />
            )}
          </Card>
        </Col>

        {/* 右侧：设备列表 */}
        <Col span={16}>
          <Card
            className="content-card"
            title={
              selectedCategoryId ? (
                <Space>
                  <FolderOutlined />
                  <span>{selectedCategoryName}</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>的设备</Text>
                </Space>
              ) : (
                <Space>
                  <FolderOutlined />
                  <span>请选择目录</span>
                </Space>
              )
            }
            extra={
              selectedCategoryId && (
                <Space>
                  <span style={{ fontSize: 12, color: '#666' }}>包含下级:</span>
                  <Switch 
                    size="small"
                    checked={includeChildren} 
                    onChange={setIncludeChildren}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => selectedCategoryId && fetchCategoryDevices(selectedCategoryId)}
                    loading={loadingDevices}
                    size="small"
                  >
                    刷新
                  </Button>
                </Space>
              )
            }
            bodyStyle={{ padding: '12px' }}
          >
            {!selectedCategoryId ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
                <FolderOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>请在左侧选择一个目录以查看设备</div>
              </div>
            ) : (
              <>
                {/* 统计信息 */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={4}>
                    <Statistic title="总数" value={deviceStats.total} valueStyle={{ fontSize: 20 }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="在线" value={deviceStats.online} valueStyle={{ fontSize: 20, color: '#52c41a' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="最近活跃" value={deviceStats.recent} valueStyle={{ fontSize: 20, color: '#faad14' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="离线" value={deviceStats.offline} valueStyle={{ fontSize: 20, color: '#999' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="已禁用" value={deviceStats.disabled} valueStyle={{ fontSize: 20, color: '#ff4d4f' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="已选中" value={selectedDeviceIds.length} valueStyle={{ fontSize: 20, color: '#1890ff' }} />
                  </Col>
                </Row>

                {/* 操作栏 */}
                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                  <Col>
                    <Space>
                      <span>状态筛选:</span>
                      <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        style={{ width: 120 }}
                        size="small"
                      >
                        <Option value="all">全部</Option>
                        <Option value="online">在线</Option>
                        <Option value="recent">最近活跃</Option>
                        <Option value="offline">离线</Option>
                        <Option value="disabled">已禁用</Option>
                      </Select>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        显示 {filteredDevices.length} 条
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      {selectedDeviceIds.length > 0 && (
                        <Button
                          size="small"
                          onClick={() => setSelectedDeviceIds([])}
                        >
                          取消选择
                        </Button>
                      )}
                      <Button
                        type="primary"
                        icon={<SwapOutlined />}
                        onClick={openMoveModal}
                        disabled={selectedDeviceIds.length === 0}
                        size="small"
                      >
                        移动到... ({selectedDeviceIds.length})
                      </Button>
                    </Space>
                  </Col>
                </Row>

                {/* 设备表格 */}
                <Table
                  columns={deviceColumns}
                  dataSource={filteredDevices}
                  rowKey="id"
                  rowSelection={rowSelection}
                  loading={loadingDevices}
                  size="small"
                  pagination={{
                    size: 'small',
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条`,
                    defaultPageSize: 20,
                    pageSizeOptions: ['10', '20', '50', '100']
                  }}
                  scroll={{ y: 'calc(100vh - 520px)' }}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* 编辑/新增目录 Modal */}
      <Modal
        title={editingCategory ? '编辑目录' : (selectedParent ? `在 "${selectedParent.name}" 下新增子目录` : '新增根目录')}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical" preserve={true}>
          <Form.Item
            name="name"
            label="目录名称"
            rules={[
              { required: true, message: '请输入目录名称' },
              { max: 100, message: '名称长度不能超过100个字符' }
            ]}
          >
            <Input placeholder="请输入目录名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[{ max: 500, message: '描述长度不能超过500个字符' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入目录描述" />
          </Form.Item>

          {!selectedParent && (
            <Form.Item name="parentId" label="上级目录">
              <Select placeholder="请选择上级目录（可留空为根目录）" allowClear>
                {categories.map(category => (
                  <Option key={category.id} value={category.id} disabled={editingCategory?.id === category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedParent && (
            <>
              <Form.Item name="parentId" style={{ display: 'none' }}>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item label="上级目录">
                <Input value={selectedParent.name} disabled style={{ backgroundColor: '#f5f5f5' }} />
              </Form.Item>
            </>
          )}

          <Form.Item name="versionId" label="默认版本">
            <Select placeholder="请选择默认版本（可留空）" allowClear>
              {versions.map(version => (
                <Option key={version.id} value={version.id}>
                  v{version.versionCode} - {version.description || '无描述'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="policyId" label="默认管控措施">
            <Select placeholder="请选择默认管控措施（可留空）" allowClear>
              {policies.map(policy => (
                <Option key={policy.id} value={policy.id}>
                  {policy.name} - {policy.description || '无描述'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>取消</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingCategory ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 移动设备 Modal */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            移动设备到目录
          </Space>
        }
        open={moveModalVisible}
        onCancel={() => setMoveModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setMoveModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleBatchMove}
            loading={movingDevices}
            disabled={!targetCategoryId}
          >
            确认移动 ({selectedDeviceIds.length} 台)
          </Button>
        ]}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>已选择 <strong>{selectedDeviceIds.length}</strong> 台设备，请选择目标目录：</Text>
        </div>
        
        <Form layout="vertical">
          <Form.Item label="目标目录" required>
            <Select
              placeholder="请选择目标目录"
              value={targetCategoryId}
              onChange={setTargetCategoryId}
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {flattenCategories(categories).map(({ category, level }) => (
                <Option 
                  key={category.id} 
                  value={category.id}
                  disabled={!includeChildren && category.id === selectedCategoryId}
                >
                  <span style={{ paddingLeft: level * 16 }}>
                    {level > 0 && '└ '}
                    {category.name}
                    {!includeChildren && category.id === selectedCategoryId && ' (当前目录)'}
                  </span>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {targetCategoryId && (
          <Alert
            message={
              <span>
                设备将移动到: <strong>{getCategoryPath(targetCategoryId)}</strong>
              </span>
            }
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        )}
      </Modal>
    </div>
  );
};

export default Categories;
